
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { GeoJSON, useMap, Marker, Popup, Tooltip } from 'react-leaflet';
import { GpxRoute, RouteDifficulty, AppUser, Coordinates, RouteRegion, WaypointIcon } from '../../types';
import { WAYPOINT_ICONS } from '../../services/waypointService';
import { fetchGpxWithProxy } from '../../services/firebaseService';
import { gpx } from '@tmcw/togeojson';
import { CheckCircle2, Activity, AlertTriangle, Flame, Mountain, Trophy } from 'lucide-react';
import L from 'leaflet';
import { getDistance } from '../../services/geometryUtils';
import { ROUTE_COLOR_PALETTE, TERRAIN_META } from '../../constants';
import { fetchWikiContext } from '../../services/wikiService';
import { detectCountry } from '../../utils/countryDetection';
import { isMapAlive } from './MapUtils';

interface RouteLayerProps {
  user: AppUser | null;
  onOpenAuth: () => void;
  onInteraction?: () => void;
  active?: boolean;
  onAnalyzeRoute?: (coords: Coordinates[], routeId: string) => void;
  onLoadCachedSpots?: (spotIds: string[]) => void;
  onPeakClick?: (coords: Coordinates) => void;
  onRouteActivated?: (coords: Coordinates[] | null) => void;
  isAnalyzing?: boolean;
  onRouteClick?: (routeId: string) => void;

  // New Props from App
  passedRoutes?: GpxRoute[];
  passedActiveRouteIds?: Set<string>;
  onSetActiveRouteIds?: (ids: Set<string>) => void;
  passedPreviewRegion?: RouteRegion | null;
  onRegionBoundsFound?: (bounds: L.LatLngBounds) => void;
  onRegionLoading?: (loading: boolean) => void;
  selectedRouteId?: string | null;
  previewFilteredRouteIds?: Set<string> | null;
  isFollowingRoute?: boolean;
}

interface RoutePeak {
  lat: number;
  lng: number;
  elevation: number;
  isHighest: boolean;
}

const DIFFICULTY_CONFIG: Record<RouteDifficulty, { color: string, icon: React.ReactNode }> = {
  'Easy': { color: '#f59e0b', icon: <CheckCircle2 size={12} /> },    // amber — visible on green topo
  'Medium': { color: '#3b82f6', icon: <Activity size={12} /> },
  'Hard': { color: '#ef4444', icon: <AlertTriangle size={12} /> },
  'Extreme': { color: '#7f1d1d', icon: <Flame size={12} /> }
};

/** Terrain type → color for multi-colored route rendering (derived from shared TERRAIN_META) */
const TERRAIN_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(TERRAIN_META).map(([k, v]) => [k, v.color])
);

/** Get route line color based on vehicle suitability. */
const getRouteColor = (route: GpxRoute, userCategory?: string): string => {
  if (!route.suitableCategories?.length) {
    return DIFFICULTY_CONFIG[route.difficulty]?.color || '#3b82f6';
  }
  if (!userCategory) return '#06b6d4'; // cyan — visible on green topo
  return route.suitableCategories.includes(userCategory as any) ? '#06b6d4' : '#ef4444';
};

/** Check if current month is winter (Nov-Apr) */
const isWinterNow = (): boolean => {
  const m = new Date().getMonth(); // 0-indexed
  return m >= 10 || m <= 3; // Nov, Dec, Jan, Feb, Mar, Apr
};

/** Get opacity adjustment for winter-unsuitable routes */
const getSeasonalOpacity = (route: GpxRoute): number => {
  if (!isWinterNow()) return 1;
  if (route.winterSuitability === 'not_recommended') return 0.4;
  if (route.winterSuitability === 'limited') return 0.7;
  return 1;
};

/** Get dash pattern based on primary terrain type */
const getTerrainDashArray = (route: GpxRoute): string | undefined => {
  // Winter: override with dotted pattern for unsuitable routes
  if (isWinterNow() && route.winterSuitability === 'not_recommended') return '4 8';
  if (!route.terrainTypes?.length) return undefined; // solid line
  const primary = route.terrainTypes[0];
  if (primary === 'paved') return undefined; // solid
  if (primary === 'gravel') return '12 6';
  if (primary === 'forest') return '8 4 2 4';
  if (primary === 'sand') return '4 4';
  if (primary === 'snow') return '2 6';
  if (primary === 'mountain') return '16 4 4 4';
  if (primary === 'coastal') return '10 4';
  return undefined;
};

const createPeakIcon = (isHighest: boolean, elevation: number) => {
  const color = isHighest ? '#fbbf24' : '#78716c'; // Amber-400 vs Stone-500
  const size = isHighest ? 36 : 28;
  const iconSize = isHighest ? 18 : 14;

  return L.divIcon({
    className: 'custom-peak-marker',
    html: `
      <div style="position: relative; width: ${size}px; height: ${size}px; display: flex; align-items: center; justify-content: center;">
        ${isHighest ? `<div style="position: absolute; inset: 0; background: ${color}; opacity: 0.3; border-radius: 50%; animation: ping 3s cubic-bezier(0,0,0.2,1) infinite;"></div>` : ''}
        <div style="position: relative; width: ${size}px; height: ${size}px; background: ${color}; border: 2px solid white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 6px rgba(0,0,0,0.3); z-index: 10;">
          <svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"/></svg>
        </div>
        <div style="position: absolute; bottom: -8px; background: white; padding: 1px 4px; border-radius: 4px; font-size: 8px; font-weight: 900; color: ${isHighest ? '#92400e' : '#44403c'}; box-shadow: 0 2px 4px rgba(0,0,0,0.1); border: 1px solid ${color}; white-space: nowrap; z-index: 20;">
          ${Math.round(elevation)}m
        </div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

const createRouteWaypointIcon = (icon: WaypointIcon) => {
  const meta = WAYPOINT_ICONS.find(w => w.id === icon) ?? WAYPOINT_ICONS[0];
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${meta.defaultColor}20;border:1.5px solid ${meta.defaultColor};display:flex;align-items:center;justify-content:center;font-size:8px;line-height:1;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${meta.emoji}</div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -7],
  });
};

// Filter out Point features from GeoJSON so Leaflet doesn't render default blue markers
const lineFilter = (feature: any) => feature?.geometry?.type !== 'Point';

const RouteLayer: React.FC<RouteLayerProps> = ({
  user, onOpenAuth, onInteraction, active = false, onAnalyzeRoute, onLoadCachedSpots, onPeakClick, onRouteActivated, isAnalyzing, onRouteClick,
  passedRoutes = [], passedActiveRouteIds = new Set<string>(), onSetActiveRouteIds, passedPreviewRegion, onRegionBoundsFound, onRegionLoading, selectedRouteId, previewFilteredRouteIds, isFollowingRoute = false
}) => {
  const [geoJsonData, setGeoJsonData] = useState<Record<string, any>>({});
  const [routePeaks, setRoutePeaks] = useState<Record<string, RoutePeak[]>>({});
  const [routeNorthPoints, setRouteNorthPoints] = useState<Record<string, Coordinates>>({});
  const [loadingRouteId, setLoadingRouteId] = useState<string | null>(null);

  const dataCacheRef = useRef<Record<string, any>>({});
  const markerRefs = useRef<Record<string, L.Marker | null>>({});
  const map = useMap();

  const groupedRoutes = useMemo(() => {
    const groups: Record<RouteRegion, GpxRoute[]> = {
      'Nord-Norge': [],
      'Midt-Norge': [],
      'Sør-Norge': [],
      'Usortert': [],
      'north': [],
      'central': [],
      'south': [],
      'unsorted': [],
      'Sverige': [],
      'Danmark': [],
      'Finland': [],
      'Island': [],
      'Norrland': [],
      'Svealand': [],
      'Götaland': [],
    };
    const REGION_COUNTRY: Record<string, string> = {
      'Nord-Norge': 'NO', 'Midt-Norge': 'NO', 'Sør-Norge': 'NO',
      'Norrland': 'SE', 'Svealand': 'SE', 'Götaland': 'SE', 'Sverige': 'SE',
      'Danmark': 'DK', 'Finland': 'FI', 'Island': 'IS',
    };
    const COUNTRY_FALLBACK: Record<string, string> = {
      'NO': 'Sør-Norge', 'SE': 'Götaland', 'DK': 'Danmark', 'FI': 'Finland',
    };

    passedRoutes.forEach(r => {
      const coord = r.center || r.startCoords;
      const detectedCountry = coord ? detectCountry(coord.latitude, coord.longitude) : null;

      if (r.region && groups[r.region]) {
        const regionCountry = REGION_COUNTRY[r.region];
        if (detectedCountry && regionCountry && detectedCountry !== regionCountry) {
          const fallback = COUNTRY_FALLBACK[detectedCountry];
          if (fallback && groups[fallback]) { groups[fallback].push(r); return; }
        }
        groups[r.region].push(r);
      } else if (detectedCountry) {
        const fallback = COUNTRY_FALLBACK[detectedCountry];
        if (fallback && groups[fallback]) { groups[fallback].push(r); return; }
        groups['Usortert'].push(r);
      } else {
        groups['Usortert'].push(r);
      }
    });
    return groups;
  }, [passedRoutes]);

  // Douglas-Peucker simplification for coordinate arrays
  const simplifyCoords = (coords: number[][], tolerance: number): number[][] => {
    if (coords.length <= 2) return coords;

    let maxDist = 0;
    let maxIdx = 0;
    const first = coords[0];
    const last = coords[coords.length - 1];

    for (let i = 1; i < coords.length - 1; i++) {
      const d = perpendicularDist(coords[i], first, last);
      if (d > maxDist) { maxDist = d; maxIdx = i; }
    }

    if (maxDist > tolerance) {
      const left = simplifyCoords(coords.slice(0, maxIdx + 1), tolerance);
      const right = simplifyCoords(coords.slice(maxIdx), tolerance);
      return [...left.slice(0, -1), ...right];
    }
    return [first, last];
  };

  const perpendicularDist = (pt: number[], lineA: number[], lineB: number[]): number => {
    const dx = lineB[0] - lineA[0];
    const dy = lineB[1] - lineA[1];
    if (dx === 0 && dy === 0) return Math.hypot(pt[0] - lineA[0], pt[1] - lineA[1]);
    const t = Math.max(0, Math.min(1, ((pt[0] - lineA[0]) * dx + (pt[1] - lineA[1]) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(pt[0] - (lineA[0] + t * dx), pt[1] - (lineA[1] + t * dy));
  };

  // Simplify a GeoJSON FeatureCollection, keeping max ~2000 points per feature
  const simplifyGeoJson = (geoJson: any): any => {
    const MAX_POINTS = 2000;
    return {
      ...geoJson,
      features: geoJson.features.map((f: any) => {
        if (!f.geometry?.coordinates) return f;
        if (f.geometry.type === 'LineString') {
          const coords = f.geometry.coordinates;
          if (coords.length <= MAX_POINTS) return f;
          // Tolerance in degrees — ~0.0001° ≈ 11m, scale up for longer routes
          const tolerance = 0.0001 * Math.max(1, coords.length / MAX_POINTS);
          return { ...f, geometry: { ...f.geometry, coordinates: simplifyCoords(coords, tolerance) } };
        }
        if (f.geometry.type === 'MultiLineString') {
          const simplified = f.geometry.coordinates.map((line: number[][]) => {
            if (line.length <= MAX_POINTS) return line;
            const tolerance = 0.0001 * Math.max(1, line.length / MAX_POINTS);
            return simplifyCoords(line, tolerance);
          });
          return { ...f, geometry: { ...f.geometry, coordinates: simplified } };
        }
        return f;
      }),
    };
  };

  const loadRouteData = async (route: GpxRoute, silent: boolean = false): Promise<any> => {
    const routeId = String(route.id);

    // Check cache first
    if (dataCacheRef.current[routeId]) {
      const cached = dataCacheRef.current[routeId];
      setGeoJsonData(prev => ({ ...prev, [routeId]: cached }));
      if (!routePeaks[routeId]) {
        const peaks = analyzeRoutePeaks(cached);
        setRoutePeaks(prev => ({ ...prev, [routeId]: peaks }));
      }
      processRouteGeometry(cached, routeId);
      return cached;
    }

    if (!silent) setLoadingRouteId(routeId);

    try {
      const textData = (await fetchGpxWithProxy(String(route.gpxUrl))) as string;
      // Yield to UI thread before heavy parsing
      await new Promise(r => setTimeout(r, 0));
      const dom = new DOMParser().parseFromString(textData, "text/xml");
      const converted = gpx(dom) as any;

      if (!converted || !converted.features || converted.features.length === 0) {
        throw new Error("Kunne ikke lese spor fra filen.");
      }

      // Analyze peaks and bounds from full-resolution data
      const peaks = analyzeRoutePeaks(converted);
      setRoutePeaks(prev => ({ ...prev, [routeId]: peaks }));
      processRouteGeometry(converted, routeId);

      // Simplify for map rendering to prevent UI freeze on long routes
      const simplified = simplifyGeoJson(converted);

      dataCacheRef.current[routeId] = simplified;
      setGeoJsonData(prev => ({ ...prev, [routeId]: simplified }));

      return simplified;
    } catch (err) {
      console.warn("Feil ved lasting av rute:", err);
      return null;
    } finally {
      if (!silent) setLoadingRouteId(null);
    }
  };

  // 1. REGION PREVIEW LOGIC: Load routes in region with limited concurrency and zoom out
  // Track which region we've already zoomed to, so we don't re-zoom when groupedRoutes recalculates
  const lastZoomedRegionRef = useRef<string | null>(null);

  useEffect(() => {
    if (passedPreviewRegion) {
      const regionRoutes = groupedRoutes[passedPreviewRegion] || [];
      if (regionRoutes.length === 0) return;

      // Only zoom if this is a NEW region selection
      const shouldZoom = lastZoomedRegionRef.current !== passedPreviewRegion;

      onRegionLoading?.(true);
      let cancelled = false;

      const loadWithConcurrency = async (routes: GpxRoute[], concurrency: number) => {
        const results: any[] = new Array(routes.length).fill(null);
        let cursor = 0;
        const worker = async () => {
          while (cursor < routes.length && !cancelled) {
            const idx = cursor++;
            results[idx] = await loadRouteData(routes[idx], true);
          }
        };
        await Promise.all(Array.from({ length: Math.min(concurrency, routes.length) }, () => worker()));
        return results;
      };

      loadWithConcurrency(regionRoutes, 4)
        .then((loadedData) => {
          if (cancelled) return;
          if (!shouldZoom) return; // Don't re-zoom on memo recalc

          // Calculate bounding box for ALL loaded routes in the region
          let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
          let hasValidBounds = false;

          loadedData.forEach(data => {
            if (!data || !data.features) return;
            data.features.forEach((f: any) => {
              if (f.geometry && f.geometry.coordinates) {
                const coords = f.geometry.type === 'LineString' ? f.geometry.coordinates :
                  f.geometry.type === 'MultiLineString' ? f.geometry.coordinates.flat() : [];
                coords.forEach((c: any) => {
                  const lon = c[0];
                  const lat = c[1];
                  if (lat < minLat) minLat = lat;
                  if (lat > maxLat) maxLat = lat;
                  if (lon < minLon) minLon = lon;
                  if (lon > maxLon) maxLon = lon;
                  hasValidBounds = true;
                });
              }
            });
          });

          if (hasValidBounds) {
            lastZoomedRegionRef.current = passedPreviewRegion;
            const bounds = L.latLngBounds([minLat, minLon], [maxLat, maxLon]);
            if (isMapAlive(map)) {
              map.flyToBounds(bounds, {
                padding: [50, 50],
                duration: 1.5,
                easeLinearity: 0.25
              });
            }
            if (onRegionBoundsFound) onRegionBoundsFound(bounds);
          }
        })
        .finally(() => {
          onRegionLoading?.(false);
        });

      return () => { cancelled = true; };
    } else {
      lastZoomedRegionRef.current = null;
    }
  }, [passedPreviewRegion, groupedRoutes, map]);

  // 2. ACTIVE ROUTE LOGIC: Load specific route and zoom to it
  useEffect(() => {
    passedActiveRouteIds.forEach(async (id) => {
      const route = passedRoutes.find(r => r.id === id);
      // Only zoom if we have a single active route (User clicked a card)
      // If we have existing data, we can zoom immediately. If not, loadRouteData will return it.
      if (route && passedActiveRouteIds.size === 1) {
        let data = geoJsonData[id];

        if (!data) {
          data = await loadRouteData(route);
        }

        // Calculate bounds for this specific route and zoom
        if (data) {
          const bounds = processRouteGeometry(data, id);
          if (bounds) {
            smartFlyToBounds(bounds);

            // Fetch Wiki Context for the start of the route
            if (routeNorthPoints[id]) {
              fetchWikiContext(routeNorthPoints[id]);
            } else {
              // Fallback to center of bounds if north point not set yet
              const center = bounds.getCenter();
              fetchWikiContext({ latitude: center.lat, longitude: center.lng });
            }
          }
        }
      } else if (route && !geoJsonData[id]) {
        // Background loading if multiple active
        loadRouteData(route);
      }
    });
  }, [passedActiveRouteIds, passedRoutes, map]);

  useEffect(() => {
    if (!onRouteActivated) return;
    if (passedActiveRouteIds.size === 0) {
      onRouteActivated(null);
      return;
    }

    let allCoords: Coordinates[] = [];
    let allDataLoaded = true;
    passedActiveRouteIds.forEach(id => {
      const data = geoJsonData[id];
      if (data && data.features) {
        data.features.forEach((f: any) => {
          if (f.geometry && f.geometry.coordinates) {
            const coords = f.geometry.type === 'LineString' ? f.geometry.coordinates :
              f.geometry.type === 'MultiLineString' ? f.geometry.coordinates.flat() : [];
            coords.forEach((c: any) => {
              allCoords.push({ longitude: c[0], latitude: c[1] });
            });
          }
        });
      } else {
        allDataLoaded = false;
      }
    });

    if (allCoords.length > 0) {
      onRouteActivated(allCoords);
    } else if (allDataLoaded) {
      // Only send null when all data is loaded but genuinely empty
      onRouteActivated(null);
    }
    // If data is still loading, don't send null prematurely
  }, [passedActiveRouteIds, geoJsonData]);

  const processRouteGeometry = (geoJSON: any, routeId: string) => {
    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    let found = false;
    let northPoint: Coordinates | null = null;

    if (geoJSON.features) {
      geoJSON.features.forEach((f: any) => {
        if (f.geometry && f.geometry.coordinates) {
          const coords = f.geometry.type === 'LineString' ? f.geometry.coordinates :
            f.geometry.type === 'MultiLineString' ? f.geometry.coordinates.flat() : [];

          coords.forEach((c: any) => {
            const lon = c[0];
            const lat = c[1];
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) {
              maxLat = lat;
              northPoint = { latitude: lat, longitude: lon };
            }
            if (lon < minLon) minLon = lon;
            if (lon > maxLon) maxLon = lon;
            found = true;
          });
        }
      });
    }

    if (!found) return null;

    if (northPoint) {
      setRouteNorthPoints(prev => ({ ...prev, [routeId]: northPoint! }));
    }

    return L.latLngBounds([minLat, minLon], [maxLat, maxLon]);
  };

  const analyzeRoutePeaks = (geoJSON: any): RoutePeak[] => {
    const allPoints: { lat: number, lng: number, ele: number }[] = [];

    if (geoJSON.features) {
      geoJSON.features.forEach((f: any) => {
        if (f.geometry && f.geometry.coordinates) {
          const coords = f.geometry.type === 'LineString' ? f.geometry.coordinates :
            f.geometry.type === 'MultiLineString' ? f.geometry.coordinates.flat() : [];

          coords.forEach((c: any) => {
            if (c.length >= 3 && c[2] > 400) {
              allPoints.push({ lng: c[0], lat: c[1], ele: c[2] });
            }
          });
        }
      });
    }

    if (allPoints.length === 0) return [];

    allPoints.sort((a, b) => b.ele - a.ele);

    const peaks: RoutePeak[] = [];
    const highest = allPoints[0];
    peaks.push({ lat: highest.lat, lng: highest.lng, elevation: highest.ele, isHighest: true });

    for (let i = 1; i < allPoints.length; i++) {
      if (peaks.length >= 3) break;
      const p = allPoints[i];
      if (p.ele < highest.ele * 0.8) continue;
      const isFarEnough = peaks.every(peak =>
        getDistance(p.lat, p.lng, peak.lat, peak.lng) > 3000
      );
      if (isFarEnough) {
        peaks.push({ lat: p.lat, lng: p.lng, elevation: p.ele, isHighest: false });
      }
    }

    return peaks;
  };

  const smartFlyToBounds = (bounds: L.LatLngBounds) => {
    // Small delay to ensure map container has correct size after mobile view transitions
    setTimeout(() => {
      if (!isMapAlive(map)) return;
      map.invalidateSize();
      if (bounds.isValid()) {
        map.flyToBounds(bounds, {
          padding: [50, 50],
          duration: 1.5,
          easeLinearity: 0.25
        });
      }
    }, 200);
  };

  const triggerAutoAnalysis = (geoJson: any, routeId: string, hasCachedSpots: boolean) => {
    if (hasCachedSpots && onLoadCachedSpots) {
      return;
    }
    if (!geoJson || !onAnalyzeRoute) return;
    const coords: Coordinates[] = [];
    if (geoJson.features) {
      geoJson.features.forEach((f: any) => {
        if (f.geometry && f.geometry.coordinates) {
          const c = f.geometry.type === 'LineString' ? f.geometry.coordinates :
            f.geometry.type === 'MultiLineString' ? f.geometry.coordinates.flat() : [];
          c.forEach((p: any) => coords.push({ longitude: p[0], latitude: p[1] }));
        }
      });
    }
    if (coords.length > 0) {
      onAnalyzeRoute(coords, routeId);
    }
  };

  const toggleRoute = async (route: GpxRoute) => {
    if (!user) { onOpenAuth(); return; }
    if (!onSetActiveRouteIds) return;

    const routeId = String(route.id);
    const isCurrentlyActive = passedActiveRouteIds.has(routeId);
    const newActive = new Set(passedActiveRouteIds);

    if (isCurrentlyActive) {
      newActive.delete(routeId);
      onSetActiveRouteIds(newActive);
    } else {
      onInteraction?.();
      const hasCachedSpots = !!(route.associatedSpotIds && route.associatedSpotIds.length > 0);
      const data = await loadRouteData(route);

      if (data) {
        newActive.add(routeId);
        onSetActiveRouteIds(newActive);

        if (hasCachedSpots && onLoadCachedSpots) {
          onLoadCachedSpots(route.associatedSpotIds!);
        } else {
          triggerAutoAnalysis(data, routeId, false);
        }
      }
    }
  };

  if (!active) return null;

  return (
    <>
      {/* Render Active Routes (Solid) */}
      {Array.from(passedActiveRouteIds).map((id: string) => {
        const data = geoJsonData[id];
        const route = passedRoutes.find(r => String(r.id) === id);
        const peaks = routePeaks[id] || [];
        const northPoint = routeNorthPoints[id];

        if (!data || !route) return null;

        // During follow mode, hide the styled GeoJSON route line
        // (the follow polyline in Map.tsx handles route rendering with driven/remaining split)
        if (isFollowingRoute) return null;

        const terrainTypes = route.terrainTypes || [];
        const hasMultipleTerrain = terrainTypes.length > 1;
        const dashArray = getTerrainDashArray(route);

        return (
          <React.Fragment key={`active-${id}`}>
            {/* Terrain underlines — one per terrain type, offset with dashArray */}
            {hasMultipleTerrain && terrainTypes.map((terrain, tIdx) => {
              const tColor = TERRAIN_COLORS[terrain] || '#78716c';
              const segLen = Math.max(8, Math.round(40 / terrainTypes.length));
              const gapLen = Math.round(segLen * (terrainTypes.length - 1));
              const offset = tIdx * segLen;
              return (
                <GeoJSON
                  key={`terrain-${id}-${terrain}`}
                  data={data}
                  filter={lineFilter}
                  style={() => ({
                    color: tColor,
                    weight: 10,
                    opacity: 0.35,
                    lineJoin: 'round',
                    lineCap: 'butt',
                    dashArray: `${segLen} ${gapLen}`,
                    dashOffset: `${offset}`,
                    interactive: false,
                  })}
                />
              );
            })}

            {/* White outline for contrast against green topo map */}
            <GeoJSON
              data={data}
              filter={lineFilter}
              style={() => ({
                color: '#ffffff',
                weight: (hasMultipleTerrain ? 5 : 7) + 4,
                opacity: 0.6 * getSeasonalOpacity(route),
                lineJoin: 'round',
                lineCap: 'round',
                dashArray: !hasMultipleTerrain ? dashArray : undefined,
                interactive: false,
              })}
            />

            {/* Main Visible Line */}
            <GeoJSON
              data={data}
              filter={lineFilter}
              style={() => ({
                color: getRouteColor(route, user?.activeVehicleCategory),
                weight: hasMultipleTerrain ? 5 : 7,
                opacity: getSeasonalOpacity(route),
                lineJoin: 'round',
                lineCap: 'round',
                dashArray: !hasMultipleTerrain ? dashArray : undefined,
                className: 'leaflet-interactive leaflet-route-line'
              })}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e.originalEvent);
                  onInteraction?.();
                  onRouteClick?.(id);
                }
              }}
            />

            {/* Wider invisible click-area */}
            <GeoJSON
              data={data}
              filter={lineFilter}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e.originalEvent);
                  onInteraction?.();
                  onRouteClick?.(id);
                }
              }}
              style={{ weight: 22, opacity: 0, color: 'transparent', className: 'leaflet-interactive' }}
            />

            {/* Start Marker */}
            {northPoint && (
              <Marker
                position={[northPoint.latitude, northPoint.longitude]}
                opacity={0}
                ref={(ref) => { if (ref) markerRefs.current[id] = ref; }}
                eventHandlers={{
                  click: (e) => {
                    L.DomEvent.stop(e);
                    onInteraction?.();
                    onRouteClick?.(id);
                  }
                }}
              />
            )}

            {peaks.map((peak, idx) => (
              <Marker
                key={`peak-${id}-${idx}`}
                position={[peak.lat, peak.lng]}
                icon={createPeakIcon(peak.isHighest, peak.elevation)}
                eventHandlers={{
                  click: (e) => {
                    L.DomEvent.stop(e);
                    onPeakClick?.({ latitude: peak.lat, longitude: peak.lng });
                  }
                }}
              />
            ))}

            {selectedRouteId === id && route.routeWaypoints?.map((wp, wpIdx) => (
              <Marker
                key={`rwp-${id}-${wpIdx}`}
                position={[wp.latitude, wp.longitude]}
                icon={createRouteWaypointIcon(wp.icon)}
              >
                <Popup>
                  <div style={{ minWidth: 100 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#1c1917', margin: 0 }}>{wp.name}</p>
                    {wp.description && <p style={{ fontSize: 10, color: '#78716c', margin: '2px 0 0' }}>{wp.description}</p>}
                    {wp.elevation != null && <p style={{ fontSize: 9, color: '#a8a29e', margin: '2px 0 0' }}>{Math.round(wp.elevation)} m</p>}
                  </div>
                </Popup>
              </Marker>
            ))}
          </React.Fragment>
        );
      })}

      {/* Render Preview Routes */}
      {passedPreviewRegion && groupedRoutes[passedPreviewRegion]?.map((route, idx) => {
        const id = String(route.id);
        if (passedActiveRouteIds.has(id)) return null;
        if (previewFilteredRouteIds && !previewFilteredRouteIds.has(id)) return null;
        const data = geoJsonData[id];
        if (!data) return null;

        const color = ROUTE_COLOR_PALETTE[idx % ROUTE_COLOR_PALETTE.length];

        return (
          <React.Fragment key={`preview-${id}`}>
            {/* White outline for preview routes */}
            <GeoJSON
              data={data}
              filter={lineFilter}
              style={() => ({
                color: '#ffffff',
                weight: 7,
                opacity: 0.4 * getSeasonalOpacity(route),
                lineJoin: 'round',
                lineCap: 'round',
                interactive: false,
              })}
            />
            <GeoJSON
              data={data}
              filter={lineFilter}
              style={() => ({
                color: color,
                weight: 4,
                opacity: 0.8 * getSeasonalOpacity(route),
                lineJoin: 'round',
                lineCap: 'round',
                dashArray: isWinterNow() && route.winterSuitability === 'not_recommended' ? '4 8' : undefined,
                className: 'leaflet-interactive route-preview'
              })}
              eventHandlers={{ click: (e) => { L.DomEvent.stop(e.originalEvent); toggleRoute(route); } }}
            />
            {/* Wider transparent hit-area */}
            <GeoJSON
              data={data}
              filter={lineFilter}
              style={{ weight: 20, opacity: 0, color: 'transparent' }}
              eventHandlers={{ click: (e) => { L.DomEvent.stop(e.originalEvent); toggleRoute(route); } }}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

export default RouteLayer;
